<template>
  <div class="wrapper">
    <div class="text-center">
      <p class="text-h4 mt-6 mb-4 primary-color">Tasks</p>
    </div>

    <v-progress-circular v-if="loading" indeterminate class="d-block mx-auto"></v-progress-circular>

    <v-card v-if="recommender" class="card-form mb-8">
      <v-card-title class="headline background-gradient d-flex align-center">
        <span class="headline ml-3">Recommender</span>
        <v-spacer></v-spacer>
        <v-btn
          icon
          dark
          :disabled="queueBusy"
          aria-label="Run all recommender tasks"
          @click="playAll"
        >
          <v-icon>mdi-play</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-6 pl-8">
        <p v-if="recommender.lastRun" class="text-body-1">
          Last local run: {{ formatDate(recommender.lastRun.finishedAt) }}
          ({{ recommender.lastRun.ok ? 'successful' : 'failed' }})
        </p>
        <p v-else class="text-body-1">No run has been recorded on this server yet.</p>
        <v-simple-table>
          <thead>
            <tr>
              <th>Task</th>
              <th>Table</th>
              <th>Type</th>
              <th>Rows now</th>
              <th>Rows in the last run</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="step in recommender.steps" :key="step.id">
              <td>{{ step.title }}</td>
              <td>{{ step.table }}</td>
              <td>{{ step.type }}</td>
              <td>{{ step.rows }}</td>
              <td>{{ rowsFromLastRun(step.id) }}</td>
              <td>
                <v-btn
                  icon
                  :disabled="queueBusy"
                  :aria-label="'Run ' + step.title"
                  @click="play(step.id, step.title)"
                >
                  <v-icon>mdi-play</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <v-card v-for="cron in crons" :key="cron.id" class="card-form mb-8">
      <v-card-title class="headline background-gradient d-flex align-center">
        <span class="headline ml-3">{{ cron.title }}</span>
        <v-spacer></v-spacer>
        <v-btn icon dark :disabled="queueBusy" :aria-label="'Run ' + cron.title" @click="play(cron.id, cron.title)">
          <v-icon>mdi-play</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-6 pl-8">
        <p class="text-body-2">{{ cron.script }}</p>
        <p class="text-body-1">{{ cron.description }}</p>
        <p v-if="cron.runs.length === 0" class="text-body-1">No recorded runs.</p>
        <template v-else>
          <p class="text-body-1">Last run: {{ formatDate(cron.runs[0].modifiedAt) }}</p>
          <ul>
            <li v-for="run in cron.runs" :key="run.file">
              {{ run.file }} — {{ formatDate(run.modifiedAt) }}
            </li>
          </ul>
          <pre v-if="cron.log" class="cron-log">{{ cron.log }}</pre>
        </template>
      </v-card-text>
    </v-card>

    <v-dialog v-model="runDialog" persistent max-width="720">
      <v-card>
        <v-card-title>{{ runTitle }}</v-card-title>
        <v-card-text>
          <template v-if="queueMode">
            <p v-if="runStatus !== 'running'" class="text-body-1">{{ runStatusLabel }}</p>
            <v-progress-linear
              :value="queueProgress"
              height="8"
              color="#047ec3"
              class="mb-4"
            ></v-progress-linear>
            <v-simple-table>
              <tbody>
                <tr v-for="item in queueItems" :key="item.id">
                  <td class="queue-status">
                    <v-progress-circular
                      v-if="item.status === 'running'"
                      indeterminate
                      size="22"
                      width="3"
                      color="#047ec3"
                    ></v-progress-circular>
                    <v-icon v-else-if="item.status === 'ok'" color="#52ae32">mdi-check</v-icon>
                    <v-icon v-else-if="item.status === 'error'" color="#c62828">mdi-close</v-icon>
                    <v-icon v-else-if="item.status === 'cancelled'" color="#757575">mdi-cancel</v-icon>
                    <v-icon v-else color="#bdbdbd">mdi-minus</v-icon>
                  </td>
                  <td>{{ item.title }}</td>
                  <td class="text-no-wrap">{{ queueStatusLabel(item.status) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <pre v-if="runOutput" class="cron-log">{{ runOutput }}</pre>
          </template>
          <div v-else-if="runStatus === 'running'" class="text-center py-6">
            <v-progress-circular indeterminate size="56" width="5" color="#047ec3"></v-progress-circular>
            <p class="text-body-1 mt-4 mb-0">Running…</p>
          </div>
          <template v-else>
            <p class="text-body-1">{{ runStatusLabel }}</p>
            <pre class="cron-log">{{ runOutput || 'No output.' }}</pre>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn v-if="runStatus === 'running'" text @click="cancelRun">Cancel</v-btn>
          <template v-else>
            <v-btn text @click="copyRunOutput">{{ copyLabel }}</v-btn>
            <v-btn class="button-gradient-half" depressed @click="closeRun">Close</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'BackofficeTasks',
  data: () => ({
    loading: false,
    recommender: null,
    crons: [],
    runDialog: false,
    runTitle: '',
    runStatus: 'running',
    runOutput: '',
    runId: null,
    pollTimer: null,
    copyLabel: 'Copy',
    copyTimer: null,
    queueMode: false,
    queueItems: [],
    queueCancelled: false
  }),
  computed: {
    queueBusy() {
      return this.runDialog && this.runStatus === 'running'
    },
    queueProgress() {
      if (!this.queueItems.length) return 0
      const done = this.queueItems.filter((item) => item.status !== 'pending' && item.status !== 'running').length
      return Math.round((done / this.queueItems.length) * 100)
    },
    runStatusLabel() {
      if (this.runStatus === 'ok') return 'Finished successfully.'
      if (this.runStatus === 'cancelled') return 'Cancelled.'
      if (this.queueMode) return 'Finished. One or more tasks failed; the rest still ran.'
      return 'Finished with an error.'
    }
  },
  async mounted() {
    await this.load()
  },
  beforeDestroy() {
    clearTimeout(this.pollTimer)
    clearTimeout(this.copyTimer)
  },
  methods: {
    formatDate(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleString('en-GB')
    },
    rowsFromLastRun(stepId) {
      const steps = this.recommender && this.recommender.lastRun && this.recommender.lastRun.steps
      if (!steps) return '—'
      const found = steps.find((step) => step.name === stepId)
      return found ? found.rows : '—'
    },
    async api(path, method, body) {
      const options = {
        method: method || 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Auth-Token': this.$cookie.get('jwt_token')
        }
      }
      if (method === 'POST') options.body = JSON.stringify(body || {})
      let response = await fetch(process.env.VUE_APP_API_ENDPOINT + path, options)
      response = await response.json()
      if (!response.success) throw new Error(response.message || 'Error')
      return response.result
    },
    queueStatusLabel(status) {
      if (status === 'running') return 'Running'
      if (status === 'ok') return 'Done'
      if (status === 'error') return 'Failed'
      if (status === 'cancelled') return 'Cancelled'
      return 'Waiting'
    },
    resetRunDialog(title) {
      this.runTitle = title
      this.runStatus = 'running'
      this.runOutput = ''
      this.runId = null
      this.copyLabel = 'Copy'
      this.queueCancelled = false
      this.runDialog = true
    },
    combinedQueueOutput() {
      return this.queueItems
        .map((item) => '— ' + item.title + ' (' + item.status + ')\n' + (item.output || '').trim())
        .join('\n\n')
    },
    async play(id, title) {
      this.queueMode = false
      this.queueItems = []
      this.resetRunDialog(title)
      try {
        const started = await this.api('backoffice/tasks/run', 'POST', { id })
        this.runId = started.runId
        this.runStatus = await this.waitForRun()
      } catch (error) {
        this.runStatus = 'error'
        this.runOutput = error.message
      }
    },
    async playAll() {
      const steps = (this.recommender && this.recommender.steps) || []
      this.queueMode = true
      this.queueItems = steps.map((step) => ({
        id: step.id,
        title: step.title,
        status: 'pending',
        output: ''
      }))
      this.resetRunDialog('Recommender')
      for (let i = 0; i < this.queueItems.length; i++) {
        const item = this.queueItems[i]
        if (this.queueCancelled) {
          item.status = 'cancelled'
          continue
        }
        item.status = 'running'
        this.runOutput = ''
        try {
          const started = await this.api('backoffice/tasks/run', 'POST', { id: item.id })
          this.runId = started.runId
          const status = await this.waitForRun()
          item.status = this.queueCancelled && status === 'cancelled' ? 'cancelled' : status
          item.output = this.runOutput
        } catch (error) {
          item.status = 'error'
          item.output = error.message
          this.runOutput = error.message
        }
        this.runId = null
      }
      this.runOutput = this.combinedQueueOutput()
      if (this.queueCancelled) this.runStatus = 'cancelled'
      else if (this.queueItems.some((item) => item.status === 'error')) this.runStatus = 'error'
      else this.runStatus = 'ok'
    },
    waitForRun() {
      return new Promise((resolve) => {
        const tick = async () => {
          if (!this.runId) {
            resolve(this.queueCancelled ? 'cancelled' : 'error')
            return
          }
          try {
            const state = await this.api('backoffice/tasks/runs/' + this.runId)
            this.runOutput = state.output
            if (state.status !== 'running') {
              resolve(state.status)
              return
            }
          } catch (error) {
            this.runOutput = error.message
            resolve('error')
            return
          }
          this.pollTimer = setTimeout(() => tick(), 1000)
        }
        tick()
      })
    },
    async cancelRun() {
      this.queueCancelled = true
      if (!this.runId) {
        this.runStatus = 'cancelled'
        if (this.queueMode) {
          this.queueItems.forEach((item) => {
            if (item.status === 'pending' || item.status === 'running') item.status = 'cancelled'
          })
          this.runOutput = this.combinedQueueOutput()
        } else {
          this.runDialog = false
        }
        return
      }
      try {
        const state = await this.api('backoffice/tasks/runs/' + this.runId + '/cancel', 'POST')
        this.runOutput = state.output
        if (!this.queueMode) this.runStatus = state.status
      } catch (error) {
        this.runOutput = error.message
        if (!this.queueMode) this.runStatus = 'error'
      }
    },
    async copyRunOutput() {
      const text = this.runOutput || 'No output.'
      let copied = false
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
          copied = true
        }
      } catch (error) {
        copied = false
      }
      if (!copied) {
        const area = document.createElement('textarea')
        area.value = text
        area.setAttribute('readonly', '')
        area.style.position = 'fixed'
        area.style.top = '0'
        area.style.left = '0'
        area.style.opacity = '0'
        document.body.appendChild(area)
        area.focus()
        area.select()
        try {
          copied = document.execCommand('copy')
        } catch (error) {
          copied = false
        }
        document.body.removeChild(area)
      }
      this.copyLabel = copied ? 'Copied' : 'Could not copy'
      clearTimeout(this.copyTimer)
      this.copyTimer = setTimeout(() => {
        this.copyLabel = 'Copy'
      }, 1500)
    },
    closeRun() {
      clearTimeout(this.pollTimer)
      this.runDialog = false
      this.queueMode = false
      this.queueItems = []
      this.queueCancelled = false
      this.load()
    },
    async load() {
      this.loading = true
      try {
        let response = await fetch(process.env.VUE_APP_API_ENDPOINT + 'backoffice/tasks', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Auth-Token': this.$cookie.get('jwt_token')
          }
        })
        response = await response.json()
        if (!response.success) throw new Error(response.message)
        this.recommender = response.result.recommender
        this.crons = response.result.crons
      } catch (error) {
        if (error.message && error.message.includes('Authentication failed')) {
          this.$router.push('/auth-error')
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.wrapper {
  max-width: 1200px;
  margin: 0 auto;
  margin-top: 40px;
}
.primary-color {
  color: #0096c7;
}
.background-gradient {
  color: white;
  box-shadow: 0 0 20px #eee;
  background-image: linear-gradient(to right, #047ec3 0%, #52ae32 200%);
}
.cron-log {
  white-space: pre-wrap;
  word-wrap: break-word;
  background: #f6f6f6;
  padding: 16px;
  margin-top: 16px;
  max-height: 320px;
  overflow: auto;
}
.button-gradient-half {
  color: white;
  background-image: linear-gradient(to right, #047ec3 0%, #52ae32 100%);
}
.queue-status {
  width: 40px;
  text-align: center;
}
</style>
